import { Component, HostBinding, Input } from '@angular/core';
import { compact, isPresent } from '@imcc/utilities';
import { IconPosition } from 'fundament/icon-position';
import { FontSize } from '../../foundation/font-size';
import { PrefixedClassList } from '../../prefixed-class-list';
import { LinkColor } from './link-color';

/**
 *
 * @property {string} prefix             - The prefix to use for the
 *                                         CSS classes.
 * @property {boolean} disabled          - Whether this link should be disabled.
 *                                         Only semantically applicable when
 *                                         using role="button".
 * @property {string[]}                  - Additional classes applied to the
 *                                         link element.
 * @property {LinkColor} linkColor       - The color of the text and icon.
 * @property {string} icon               - Name of the fundament icon.
 * @property {FontSize} fontSize         - Size of text and icon.
 * @property {IconPosition} iconPosition - Position of the icon relative to
 *                                         the link.
 *
 * @export
 * @class HyperlinkComponent
 */
@Component({
  selector: '[app-fm-hyperlink]',
  templateUrl: './hyperlink.component.html',
})
export class HyperlinkComponent {
  @Input()
  prefix = '';

  @Input()
  disabled = false;

  @Input()
  additionalClasses: string[] = [];

  @Input()
  linkColor: LinkColor = LinkColor.Blue;

  @Input()
  icon = '';

  @Input()
  fontSize: FontSize = FontSize.Medium;

  @Input()
  iconPosition: IconPosition = IconPosition.Leading;

  @HostBinding('className')
  get anchorClassNames(): string {
    return [
      new PrefixedClassList(
        this.prefix,
        ...compact([
          'link',
          `link--${this.fontSize}`,
          `link--${this.linkColor}`,
          this.disabledClass,
          this.iconPositionClass,
        ]),
      ).toString(),
      ...this.additionalClasses,
    ].join(' ');
  }

  @HostBinding('attr.aria-disabled')
  get ariaDisabledValue(): true | undefined {
    if (this.disabled) {
      return true;
    }

    return undefined;
  }

  get showIcon(): boolean {
    return isPresent(this.icon);
  }

  get disabledClass(): string | undefined {
    if (this.disabled) {
      return 'link--disabled';
    }

    return undefined;
  }

  get iconPositionClass(): string | undefined {
    if (this.showIcon) {
      return `${this.iconPosition}-icon`;
    }

    return undefined;
  }
}
